import { inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, catchError, defaultIfEmpty, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';

import { LoggingService } from '@data-terminal/base/app/services/logging.service';
import {
    InactivityMachineEntry,
    InactivityTimerDialogComponent,
    InactivityTimerDialogData,
} from '@data-terminal/feature-inactivity-tracker/components/inactivity-timer-dialog/inactivity-timer-dialog.component';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class InactivityDialogManagerService implements OnDestroy {
    readonly #dialog = inject(MatDialog);
    readonly #loggingService = inject(LoggingService);

    // Insertion-ordered map of all machines currently flagged as inactive.
    // It is the single source of truth for the one aggregating dialog: every
    // flagged machine becomes one row. A machine leaves the map when it is
    // dismissed, navigated to, or detected active again.
    private readonly entries = new Map<string, Observable<number> | undefined>();
    private readonly entriesSubj$ = new BehaviorSubject<InactivityMachineEntry[]>([]);

    // Entries ordered by inactivity time (longest inactive first). The order is
    // computed once per list-composition change from a snapshot of the timers
    // (take(1)). Because the relative gaps between elapsed times are constant,
    // this order stays correct over time, while the per-row counters keep
    // ticking independently. Snapshotting avoids machines with (almost) the same
    // time jumping around as the individual timers tick out of phase.
    private readonly sortedEntries$: Observable<InactivityMachineEntry[]> = this.entriesSubj$.pipe(
        switchMap((entries) =>
            entries.length === 0
                ? of<InactivityMachineEntry[]>([])
                : forkJoin(
                      entries.map((entry) =>
                          (entry.timer$ ?? of(0)).pipe(
                              take(1),
                              defaultIfEmpty(0),
                              map((elapsed) => ({ entry, elapsed }))
                          )
                      )
                  ).pipe(
                      map((items) => {
                          const sorted = [...items].sort((a, b) => b.elapsed - a.elapsed);
                          return sorted.map((item) => item.entry);
                      })
                  )
        )
    );

    // There is never more than one dialog: it is opened on the first flagged
    // machine and closed once the map becomes empty.
    private dialogRef?: MatDialogRef<InactivityTimerDialogComponent>;

    public addInactiveMachine(machineId: string, timer$: Observable<number> | undefined): void {
        this.entries.set(machineId, timer$);
        this.emitEntries();
        this.openDialogIfNeeded();
    }

    public removeInactiveMachine(machineId: string): void {
        if (this.entries.delete(machineId)) {
            this.emitEntries();
            this.closeDialogIfEmpty();
        }
    }

    public isDialogOpen(): boolean {
        return !!this.dialogRef;
    }

    public ngOnDestroy(): void {
        this.entries.clear();
        this.closeDialog();
    }

    private openDialogIfNeeded(): void {
        if (this.dialogRef || this.entries.size === 0) {
            return;
        }

        try {
            this.dialogRef = this.#dialog.open(InactivityTimerDialogComponent, this.buildDialogConfig());
            this.dialogRef
                .afterClosed()
                .pipe(
                    take(1),
                    catchError((error) => {
                        this.#loggingService.error('Inactivity dialog error', { payload: { error } });
                        return of(undefined);
                    })
                )
                .subscribe(() => (this.dialogRef = undefined));
        } catch (error) {
            this.#loggingService.error('Failed to open inactivity dialog', { payload: { error } });
            this.dialogRef = undefined;
        }
    }

    private closeDialogIfEmpty(): void {
        if (this.entries.size === 0) {
            this.closeDialog();
        }
    }

    // Clears the dialog reference synchronously on close so a dialog opened right
    // after the list empties is never blocked by a stale reference (afterClosed
    // resets it asynchronously).
    private closeDialog(): void {
        this.dialogRef?.close();
        this.dialogRef = undefined;
    }

    private clearAll(): void {
        this.entries.clear();
        this.emitEntries();
        this.closeDialogIfEmpty();
    }

    private buildDialogConfig(): MatDialogConfig<InactivityTimerDialogData> {
        const dialogConfig = new MatDialogConfig<InactivityTimerDialogData>();
        dialogConfig.width = '100%';
        dialogConfig.maxWidth = '99vw';
        dialogConfig.height = '100%';
        dialogConfig.maxHeight = '99vh';
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            entries$: this.sortedEntries$,
            onClear: () => this.clearAll(),
        };
        return dialogConfig;
    }

    private emitEntries(): void {
        this.entriesSubj$.next([...this.entries].map(([machineId, timer$]) => ({ machineId, timer$ })));
    }
}
